import { useEffect, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { toast } from 'sonner';
import { api, type Presence } from '../api';
import { useDeskData } from './desk';
import { Button } from './ui/button';
import './presence.css';

const dayKey = (d: Date, timeZone: string) => new Intl.DateTimeFormat('en-CA', { timeZone }).format(d);
/** "at 9:00 AM PST", "tomorrow at 9:00 AM PST" or "Nov 3 at 9:00 AM PST", on the company's calendar. */
export function nextWhen(at: string, timeZone: string, now = new Date()) {
  const when = new Date(at);
  const days = Math.round((Date.parse(dayKey(when, timeZone)) - Date.parse(dayKey(now, timeZone))) / 86_400_000);
  const time = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone, timeZoneName: 'short' }).format(when);
  const date = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone }).format(when);
  return days <= 0 ? `at ${time}` : days === 1 ? `tomorrow at ${time}` : `${date} at ${time}`;
}
const elapsed = (since: string, now: number) => {
  const s = Math.max(0, Math.floor((now - Date.parse(since)) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** The status line, refreshed on every server change; a live call's clock ticks once a second. */
export function usePresence() {
  const { data, reload } = useDeskData<Presence>('/app-api/home/presence');
  const [now, setNow] = useState(() => Date.now());
  const calling = data?.now.kind === 'calling';
  useEffect(() => {
    // A call ends without a change event only when its report is late, so a slow refresh keeps the line honest.
    const tick = setInterval(() => setNow(Date.now()), calling ? 1000 : 30_000);
    const refresh = setInterval(reload, calling ? 10_000 : 60_000);
    return () => { clearInterval(tick); clearInterval(refresh); };
  }, [calling, reload]);
  return { presence: data, now, reload };
}

function nowText(p: Presence, now: number) {
  if (p.now.kind === 'calling') return { text: `Calling ${p.now.customerName}`, clock: p.now.since ? elapsed(p.now.since, now) : null,
    href: `#calls/${encodeURIComponent(p.now.callId)}` };
  if (p.now.kind === 'working') return { text: p.now.text, clock: null, href: null };
  return { text: p.paused ? 'Paused: nothing sends until you resume' : 'Idle: nothing in progress', clock: null, href: null };
}

/**
 * What the agent is doing now and next. `compact` is the sidebar form (no pause control). Pause uses the same switch as
 * Settings (POST /app-api/sending); nothing else changes.
 */
export function PresenceLine({ presence, now, canAct = false, compact = false, onChange }: {
  presence: Presence | null; now: number; canAct?: boolean; compact?: boolean; onChange?: () => void;
}) {
  const [busy, setBusy] = useState(false);
  if (!presence) return <div className={`presence ${compact ? 'compact' : ''}`} aria-hidden><span className="presence-dot" /></div>;
  const state = presence.now.kind === 'calling' ? 'live' : presence.paused ? 'paused' : presence.now.kind === 'working' ? 'busy' : 'idle';
  const current = nowText(presence, now);
  async function pause() {
    setBusy(true);
    try {
      await api('/app-api/sending', { paused: !presence!.paused });
      toast.success(presence!.paused ? `${presence!.persona} is sending again.` : `${presence!.persona} is paused. Nothing sends until you resume.`);
      onChange?.();
    } catch (error) { toast.error(error instanceof Error ? error.message : 'Please try again.'); }
    finally { setBusy(false); }
  }
  return <div className={`presence ${state} ${compact ? 'compact' : ''}`} role="status" aria-live="polite"
    aria-label={`${presence.persona}: ${current.text}${presence.next ? `. Next: ${presence.next.text} ${nextWhen(presence.next.at, presence.timezone)}` : ''}`}>
    <span className="presence-dot" aria-hidden />
    <span className="presence-text">
      {compact && <strong className="presence-who">{presence.persona}</strong>}
      <span className="presence-now">{current.href ? <a href={current.href}>{current.text}</a> : current.text}
        {current.clock && <span className="presence-clock">{current.clock}</span>}</span>
      {presence.next && <span className="presence-next">Next: {presence.next.href
        ? <a href={presence.next.href}>{presence.next.text}</a> : presence.next.text} {nextWhen(presence.next.at, presence.timezone)}</span>}
    </span>
    {!compact && canAct && <Button size="sm" variant="ghost" className="presence-pause" disabled={busy} onClick={() => void pause()}>
      {presence.paused ? <><Play aria-hidden />Resume</> : <><Pause aria-hidden />Pause</>}</Button>}
  </div>;
}

/** The sidebar's copy of the status line, so what the agent is doing is visible from every page. */
export function SidebarPresence() {
  const { presence, now } = usePresence();
  return <PresenceLine presence={presence} now={now} compact />;
}
